import { inject, Injectable } from '@angular/core';
import { PushNotifications, PushNotificationSchema, Token } from '@capacitor/push-notifications';
import { asType, isNativeApp } from '../../utils';
import { AangemeldeUser, User } from '../../authentication/user';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { ProfielId, RegistreerVoorMeldingenDto } from 'parkour-web-app-dto';
import { PluginListenerHandle } from '@capacitor/core';
import { MeldingenService } from './meldingen.service';
import { LocalNotifications } from '@capacitor/local-notifications';
import { LoggingService } from '../../core/logging.service';
import AuthService from '../../authentication/service/auth.service';
import BackgroundDetectionService from '../../authentication/service/background-detection.service';
import { catchError, map, of, switchMap } from 'rxjs';
import { AnalyticsService } from '../../analytics/analytics.service';
import { AnalyticsEvent, trackAnalyticsEvent } from '../../analytics/analytics-event.model';
import { isSupportedMelding } from '../model/meldingen';
import { ProfielService } from './profiel.service';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class PushNotificatieService {
  tokenRegistrationSubscription?: PluginListenerHandle;

  private readonly router = inject(Router);
  private readonly profielService = inject(ProfielService);
  private readonly meldingenService = inject(MeldingenService);
  private readonly authService = inject(AuthService);
  private readonly loggingService = inject(LoggingService);
  private readonly backgroundDetectionService = inject(BackgroundDetectionService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly http = inject(HttpClient);

  constructor() {}

  private async attemptRegisterForNotifications(user: User) {
    if (user.type === 'aangemeld' && isNativeApp()) {
      await this.registerForPushNotifications(user);
    }
  }

  private async registerForPushNotifications(user: AangemeldeUser): Promise<void> {
    this.tokenRegistrationSubscription?.remove();
    this.tokenRegistrationSubscription = await PushNotifications.addListener(
      'registration',
      (token) => this.onTokenReceived(user, token),
    );

    let permStatus = await PushNotifications.checkPermissions();

    if (permStatus.receive === 'prompt') {
      permStatus = await PushNotifications.requestPermissions();
    }

    if (permStatus.receive === 'granted') {
      await PushNotifications.register();
    }
  }

  private onTokenReceived(user: AangemeldeUser, token: Token) {
    this.registerToken(user.profielId, token.value).subscribe();
  }

  private registerToken(persoonId: string, value: string) {
    return this.http
      .post(
        `${environment.API_BASE_URL}/api/meldingen/persoon/${persoonId}/pushNotificatieRegistratie`,
        asType<RegistreerVoorMeldingenDto>({ token: value }),
      )
      .pipe(
        trackAnalyticsEvent(
          this.analyticsService,
          new AnalyticsEvent('meldingen', 'geregistreerdVoorPushNotificaties'),
        ),
      );
  }

  public initialize() {
    if (isNativeApp()) {
      this.addHandlers();
    }

    this.authService.user$.subscribe((user) => this.attemptRegisterForNotifications(user));
  }

  async displayLocalNotification(notification: PushNotificationSchema) {
    this.loggingService.log('Attempting to schedule local notification', notification.body);
    const permissionStatus = await LocalNotifications.checkPermissions();
    if (permissionStatus.display === 'granted') {
      await LocalNotifications.schedule({
        notifications: [
          {
            title: '',
            body: notification.body || '',
            id: new Date().getUTCDate(),
            schedule: { at: new Date(new Date().getTime() + 500) },
            extra: {
              ...notification.data,
            },
          },
        ],
      });
    }
  }

  private shouldDisplayLocalNotification(meldingId: string) {
    return this.meldingenService.getMeldingOrUndefined(meldingId).pipe(
      catchError(() => of(undefined)), // If melding fails to load, we assume it should be displayed (Likely due to being active in a different profiel to that of the melding)
      map((melding) => {
        if (melding === undefined) {
          return true;
        } else if (!isSupportedMelding(melding)) {
          return true;
        } else {
          return !(
            melding.gelezen || this.meldingenService.matchesOngelezenMeldingenFilter(melding)
          );
        }
      }),
    );
  }

  private async addHandlers() {
    await PushNotifications.addListener('registrationError', (err) => {
      this.loggingService.error('Registration error: ', err.error);
    });
    await PushNotifications.addListener('pushNotificationReceived', (notification) => {
      this.loggingService.log('Push notification notification received: ', notification.data);

      this.meldingenService.fetchOngelezenMeldingen();

      const meldingId = notification.data.meldingId;

      if (meldingId) {
        this.shouldDisplayLocalNotification(meldingId).subscribe((shouldDisplay) => {
          if (shouldDisplay) {
            this.displayLocalNotification(notification);
          } else {
            this.loggingService.log('Melding kan niet getoond worden, meldingId: ', meldingId);
          }
        });
      }
    });

    await PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
      this.loggingService.log(
        'Push notification action performed: ',
        notification.notification.data,
      );

      this.backgroundDetectionService
        .waitUntilActive()
        .pipe(
          map(() => {
            if (this.authService.isHandlingTimeout()) {
              this.loggingService.log(
                'Ignoring push notification action because timeout is being handled',
              );
              return;
            }

            this.meldingenService.fetchOngelezenMeldingen();

            const meldingId = notification.notification.data.meldingId;
            const targetId = notification.notification.data.targetId;

            if (meldingId && targetId) {
              this.navigateToMelding(meldingId, targetId);
            }
          }),
        )
        .subscribe();
    });
    await LocalNotifications.addListener('localNotificationActionPerformed', (notification) => {
      this.loggingService.log('Local notification action performed', notification);

      this.backgroundDetectionService
        .waitUntilActive()
        .pipe(
          map(() => {
            if (this.authService.isHandlingTimeout()) {
              this.loggingService.log(
                'Ignoring push notification action because timeout is being handled',
              );
              return;
            }

            const meldingId = notification.notification.extra.meldingId;
            const targetId = notification.notification.extra.targetId;
            if (meldingId && targetId) {
              this.navigateToMelding(meldingId, targetId);
            }
          }),
        )
        .subscribe();
    });

    this.loggingService.log('Added push notification handlers');
  }

  private navigateToMelding(meldingId: string, profielId: ProfielId) {
    this.authService
      .getCurrentUser$()
      .pipe(
        switchMap((user) => {
          if (user.type !== 'aangemeld') {
            return this.authService
              .login({
                redirectProfielId: profielId,
              })
              .pipe(map(() => this.meldingenService.navigateToMelding(meldingId)));
          } else {
            return this.profielService.getCurrentUserProfiel$().pipe(
              switchMap((profiel) => {
                if (profiel.id !== profielId) {
                  return this.authService
                    .switchProfiel(profielId, '')
                    .pipe(map(() => this.meldingenService.navigateToMelding(meldingId)));
                } else {
                  return this.router.navigate(['app', 'me', 'profiel', 'meldingen'], {
                    queryParams: { meldingId },
                  });
                }
              }),
            );
          }
        }),
      )
      .subscribe();
  }
}
